import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Download, 
  Search, 
  X, 
  Phone, 
  Mail, 
  Calendar,
  MessageSquare,
  Building2,
  FileSpreadsheet,
  Users,
  Filter,
  RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterProperty, setFilterProperty] = useState('all');
  const [filterLeadType, setFilterLeadType] = useState('all');
  const [filterBudget, setFilterBudget] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [leads, searchTerm, startDate, endDate, filterProperty, filterLeadType, filterBudget]);

  const fetchData = async () => {
    try {
      const [leadsRes, propertiesRes] = await Promise.all([
        api.get('/leads'),
        api.get('/properties'),
      ]);
      setLeads(leadsRes.data);
      setProperties(propertiesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...leads];

    if (searchTerm) {
      filtered = filtered.filter(
        (lead) =>
          lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          lead.phone.includes(searchTerm) ||
          lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          lead.message?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (startDate) {
      filtered = filtered.filter((lead) => new Date(lead.created_at) >= new Date(startDate));
    }

    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter((lead) => new Date(lead.created_at) <= end);
    }

    if (filterProperty !== 'all') {
      filtered = filtered.filter((lead) => lead.property_id === filterProperty);
    }

    if (filterLeadType !== 'all') {
      filtered = filtered.filter((lead) => lead.lead_type === filterLeadType);
    }

    if (filterBudget !== 'all') {
      filtered = filtered.filter((lead) => {
        const budget = lead.budget?.toLowerCase() || '';
        switch (filterBudget) {
          case 'under50L':
            return budget.includes('50l') || budget.includes('30l') || budget.includes('40l');
          case '50L-1Cr':
            return budget.includes('50l') || budget.includes('1cr') || budget.includes('75l');
          case '1Cr-2Cr':
            return budget.includes('1cr') || budget.includes('2cr') || budget.includes('1.5cr');
          case 'above2Cr':
            return budget.includes('2cr') || budget.includes('3cr') || budget.includes('5cr');
          default:
            return true;
        }
      });
    }

    setFilteredLeads(filtered);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setFilterProperty('all');
    setFilterLeadType('all');
    setFilterBudget('all');
  };

  const exportToCSV = () => {
    if (filteredLeads.length === 0) {
      toast.error('No leads to export');
      return;
    }

    const headers = ['Name', 'Phone', 'Email', 'Budget', 'Property', 'Lead Type', 'Message', 'Date & Time'];
    const rows = filteredLeads.map((lead) => [
      lead.name,
      lead.phone,
      lead.email || 'N/A',
      lead.budget || 'N/A',
      lead.property_title || 'General Inquiry',
      lead.lead_type || 'inquiry',
      lead.message?.replace(/,/g, ';').replace(/\n/g, ' ') || '',
      format(new Date(lead.created_at), 'yyyy-MM-dd HH:mm:ss'),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `propvanta_leads_${format(new Date(), 'yyyy-MM-dd_HHmm')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`Exported ${filteredLeads.length} leads to CSV`);
  };

  const exportToExcel = () => {
    // For Excel, we'll create a more formatted CSV that Excel handles well
    if (filteredLeads.length === 0) {
      toast.error('No leads to export');
      return;
    }

    const headers = ['Name', 'Phone', 'Email', 'Budget', 'Property', 'Lead Type', 'Message', 'Date', 'Time'];
    const rows = filteredLeads.map((lead) => [
      lead.name,
      `="${lead.phone}"`, // Preserve phone number format in Excel
      lead.email || '',
      lead.budget || '',
      lead.property_title || 'General Inquiry',
      lead.lead_type || 'inquiry',
      lead.message?.replace(/"/g, '""').replace(/\n/g, ' ') || '',
      format(new Date(lead.created_at), 'yyyy-MM-dd'),
      format(new Date(lead.created_at), 'HH:mm:ss'),
    ]);

    const csvContent = '\ufeff' + [ // BOM for Excel UTF-8
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `propvanta_leads_${format(new Date(), 'yyyy-MM-dd_HHmm')}.xls`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`Exported ${filteredLeads.length} leads to Excel`);
  };

  const getLeadTypeLabel = (type) => {
    const types = {
      inquiry: 'General Inquiry',
      site_visit: 'Site Visit',
      builder_inquiry: 'Builder Inquiry',
      builder_site_visit: 'Builder Site Visit',
    };
    return types[type] || type || 'Inquiry';
  };

  const getLeadTypeColor = (type) => {
    const colors = {
      inquiry: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      site_visit: 'bg-green-500/20 text-green-400 border-green-500/30',
      builder_inquiry: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      builder_site_visit: 'bg-rose-gold/20 text-rose-gold border-rose-gold/30',
    };
    return colors[type] || 'bg-white/10 text-white/60 border-white/20';
  };

  return (
    <div className="space-y-6" data-testid="admin-leads">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold text-white mb-2">Lead Management</h1>
          <p className="text-white/60">
            {filteredLeads.length} of {leads.length} leads
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={exportToCSV}
            data-testid="export-csv-button"
            className="bg-transparent border border-rose-gold text-rose-gold hover:bg-rose-gold hover:text-dark"
          >
            <Download className="mr-2" size={18} />
            Export CSV
          </Button>
          <Button
            onClick={exportToExcel}
            data-testid="export-excel-button"
            className="btn-primary"
          >
            <FileSpreadsheet className="mr-2" size={18} />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="glass-effect border border-dark-lighter rounded-xl p-4">
          <p className="text-white/40 text-xs uppercase tracking-wider">Total Leads</p>
          <p className="font-heading text-3xl font-bold text-white">{leads.length}</p>
        </div>
        <div className="glass-effect border border-dark-lighter rounded-xl p-4">
          <p className="text-white/40 text-xs uppercase tracking-wider">Today</p>
          <p className="font-heading text-3xl font-bold text-green-400">
            {leads.filter(l => new Date(l.created_at).toDateString() === new Date().toDateString()).length}
          </p>
        </div>
        <div className="glass-effect border border-dark-lighter rounded-xl p-4">
          <p className="text-white/40 text-xs uppercase tracking-wider">This Week</p>
          <p className="font-heading text-3xl font-bold text-blue-400">
            {leads.filter(l => {
              const leadDate = new Date(l.created_at);
              const weekAgo = new Date();
              weekAgo.setDate(weekAgo.getDate() - 7);
              return leadDate >= weekAgo;
            }).length}
          </p>
        </div>
        <div className="glass-effect border border-dark-lighter rounded-xl p-4">
          <p className="text-white/40 text-xs uppercase tracking-wider">Site Visits</p>
          <p className="font-heading text-3xl font-bold text-rose-gold">
            {leads.filter(l => l.lead_type?.includes('site_visit')).length}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="glass-effect border border-dark-lighter rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={18} className="text-rose-gold" />
          <h3 className="text-white font-medium">Filters</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="md:col-span-2">
            <Label className="text-rose-gold text-xs uppercase tracking-wider mb-2 block">Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={18} />
              <Input
                type="text"
                data-testid="search-leads"
                placeholder="Name, phone, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-dark border-dark-lighter text-white pl-10"
              />
            </div>
          </div>

          <div>
            <Label className="text-rose-gold text-xs uppercase tracking-wider mb-2 block">Start Date</Label>
            <Input
              type="date"
              data-testid="filter-start-date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-dark border-dark-lighter text-white"
            />
          </div>

          <div>
            <Label className="text-rose-gold text-xs uppercase tracking-wider mb-2 block">End Date</Label>
            <Input
              type="date"
              data-testid="filter-end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-dark border-dark-lighter text-white"
            />
          </div>

          <div>
            <Label className="text-rose-gold text-xs uppercase tracking-wider mb-2 block">Property</Label>
            <Select value={filterProperty} onValueChange={setFilterProperty}>
              <SelectTrigger className="bg-dark border-dark-lighter text-white">
                <SelectValue placeholder="All Properties" />
              </SelectTrigger>
              <SelectContent className="bg-dark-light border-dark-lighter max-h-60">
                <SelectItem value="all" className="text-white">All Properties</SelectItem>
                {properties.map((p) => (
                  <SelectItem key={p.id} value={p.id} className="text-white">
                    {p.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-rose-gold text-xs uppercase tracking-wider mb-2 block">Lead Type</Label>
            <Select value={filterLeadType} onValueChange={setFilterLeadType}>
              <SelectTrigger className="bg-dark border-dark-lighter text-white">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent className="bg-dark-light border-dark-lighter">
                <SelectItem value="all" className="text-white">All Types</SelectItem>
                <SelectItem value="inquiry" className="text-white">General Inquiry</SelectItem>
                <SelectItem value="site_visit" className="text-white">Site Visit</SelectItem>
                <SelectItem value="builder_inquiry" className="text-white">Builder Inquiry</SelectItem>
                <SelectItem value="builder_site_visit" className="text-white">Builder Site Visit</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-dark-lighter">
          <div>
            <Label className="text-rose-gold text-xs uppercase tracking-wider mb-2 block">Budget Range</Label>
            <Select value={filterBudget} onValueChange={setFilterBudget}>
              <SelectTrigger className="bg-dark border-dark-lighter text-white w-48">
                <SelectValue placeholder="All Budgets" />
              </SelectTrigger>
              <SelectContent className="bg-dark-light border-dark-lighter">
                <SelectItem value="all" className="text-white">All Budgets</SelectItem>
                <SelectItem value="under50L" className="text-white">Under 50L</SelectItem>
                <SelectItem value="50L-1Cr" className="text-white">50L - 1Cr</SelectItem>
                <SelectItem value="1Cr-2Cr" className="text-white">1Cr - 2Cr</SelectItem>
                <SelectItem value="above2Cr" className="text-white">Above 2Cr</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={fetchData}
              variant="ghost"
              className="text-white/60 hover:text-white"
            >
              <RefreshCw size={16} className="mr-1" />
              Refresh
            </Button>
            {(searchTerm || startDate || endDate || filterProperty !== 'all' || filterLeadType !== 'all' || filterBudget !== 'all') && (
              <Button
                onClick={resetFilters}
                variant="ghost"
                className="text-white/60 hover:text-white"
              >
                <X size={16} className="mr-1" />
                Clear Filters
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Leads Table */}
      {loading ? (
        <div className="text-center py-20 text-white/60">Loading leads...</div>
      ) : filteredLeads.length > 0 ? (
        <div className="glass-effect border border-dark-lighter rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="leads-table">
              <thead>
                <tr className="border-b border-dark-lighter bg-dark">
                  <th className="px-6 py-4 text-left text-rose-gold text-xs uppercase tracking-wider font-semibold">
                    Contact Info
                  </th>
                  <th className="px-6 py-4 text-left text-rose-gold text-xs uppercase tracking-wider font-semibold">
                    Property Interest
                  </th>
                  <th className="px-6 py-4 text-left text-rose-gold text-xs uppercase tracking-wider font-semibold">
                    Budget
                  </th>
                  <th className="px-6 py-4 text-left text-rose-gold text-xs uppercase tracking-wider font-semibold">
                    Type
                  </th>
                  <th className="px-6 py-4 text-left text-rose-gold text-xs uppercase tracking-wider font-semibold">
                    Message
                  </th>
                  <th className="px-6 py-4 text-left text-rose-gold text-xs uppercase tracking-wider font-semibold">
                    Date & Time
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-lighter">
                <AnimatePresence>
                  {filteredLeads.map((lead) => (
                    <motion.tr
                      key={lead.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="hover:bg-dark/50 transition-colors cursor-pointer"
                      onClick={() => setSelectedLead(selectedLead?.id === lead.id ? null : lead)}
                      data-testid={`lead-row-${lead.id}`}
                    >
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-white font-medium">{lead.name}</p>
                          <a 
                            href={`tel:${lead.phone}`} 
                            className="text-rose-gold text-sm hover:underline flex items-center gap-1 mt-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Phone size={12} />
                            {lead.phone}
                          </a>
                          {lead.email && (
                            <a 
                              href={`mailto:${lead.email}`} 
                              className="text-white/60 text-sm hover:text-rose-gold flex items-center gap-1 mt-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Mail size={12} />
                              {lead.email}
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {lead.property_title ? (
                          <div className="flex items-center gap-2">
                            <Building2 size={14} className="text-rose-gold" />
                            <span className="text-white text-sm">{lead.property_title}</span>
                          </div>
                        ) : (
                          <span className="text-white/40 text-sm">General Inquiry</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {lead.budget ? (
                          <span className="text-white">{lead.budget}</span>
                        ) : (
                          <span className="text-white/40">Not specified</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full border ${getLeadTypeColor(lead.lead_type)}`}>
                          {getLeadTypeLabel(lead.lead_type)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-white/70 text-sm max-w-xs truncate">
                          {lead.message || '-'}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-white/60 text-sm">
                          <Calendar size={14} />
                          <div>
                            <p>{format(new Date(lead.created_at), 'MMM dd, yyyy')}</p>
                            <p className="text-white/40">{format(new Date(lead.created_at), 'hh:mm a')}</p>
                          </div>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>

          {/* Selected Lead Detail */}
          <AnimatePresence>
            {selectedLead && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-t border-dark-lighter bg-dark/50 p-6"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-rose-gold font-semibold mb-2">Full Message from {selectedLead.name}</h3>
                    <p className="text-white/80 whitespace-pre-wrap">{selectedLead.message || 'No message provided'}</p>
                  </div>
                  <Button
                    onClick={() => setSelectedLead(null)}
                    variant="ghost"
                    size="sm"
                    className="text-white/40 hover:text-white"
                  >
                    <X size={18} />
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ) : (
        <div className="text-center py-20">
          <Users className="mx-auto text-white/20 mb-4" size={64} />
          <p className="text-white/60 text-lg mb-4">
            {searchTerm || startDate || endDate || filterProperty !== 'all' || filterLeadType !== 'all' || filterBudget !== 'all'
              ? 'No leads match your filters'
              : 'No leads yet'}
          </p>
          {(searchTerm || startDate || endDate || filterProperty !== 'all' || filterLeadType !== 'all' || filterBudget !== 'all') && (
            <Button onClick={resetFilters} className="btn-primary">
              Clear Filters
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminLeads;
